import { ref, computed } from 'vue';
import { fetchViewPermissions } from '@/api/permissionsApi';

/**
 * Composable สำหรับจัดการ view permissions ของ client
 * ดึงข้อมูลจาก API: GET /api/view-permissions
 * 
 * Shared state (module-level) — เรียก API ครั้งเดียว ใช้ได้ทุก component
 * ถ้า API error → default เห็นทุกหน้า (ตามเอกสาร backend)
 */

// Default: เห็นทุกหน้า
const DEFAULT_PERMISSIONS = {
  flowaccount: true,
  peakaccount: true,
  thaibulksms: true,
  smartcomm: true,
  marketplace: true,
};

// Shared reactive state
const permissions = ref({ ...DEFAULT_PERMISSIONS });
const permissionsLoaded = ref(false);
let loadPromise = null;

/**
 * โหลด permissions จาก API (เรียกครั้งเดียว, cache ไว้)
 * @param {boolean} force - บังคับโหลดใหม่
 * @returns {Promise<void>}
 */
const loadPermissions = async (force = false) => {
  if (permissionsLoaded.value && !force) return;
  
  // ป้องกันเรียกซ้ำพร้อมกัน
  if (loadPromise && !force) return loadPromise;

  loadPromise = (async () => {
    try {
      const response = await fetchViewPermissions();
      if (response.success && response.data) {
        permissions.value = {
          flowaccount: response.data.flowaccount !== false,
          peakaccount: response.data.peakaccount !== false,
          thaibulksms: response.data.thaibulksms !== false,
          smartcomm: response.data.smartcomm !== false,
          marketplace: response.data.marketplace !== false,
        };
      } else {
        // API error → default เห็นทุกหน้า
        permissions.value = { ...DEFAULT_PERMISSIONS };
      }
    } catch (error) {
      console.error('Failed to load view permissions:', error);
      permissions.value = { ...DEFAULT_PERMISSIONS };
    } finally {
      permissionsLoaded.value = true;
      loadPromise = null;
    }
  })();

  return loadPromise;
};

/**
 * รีเซ็ต permissions (ใช้ตอน logout)
 */
const resetPermissions = () => {
  permissions.value = { ...DEFAULT_PERMISSIONS };
  permissionsLoaded.value = false;
  loadPromise = null;
};

export function usePermissions() {
  /**
   * ดึงรายการแพลตฟอร์มที่ user มีสิทธิ์เข้าถึง
   * @returns {Array<string>} รายการ platform IDs
   */
  const getUserPermissions = () => {
    return Object.entries(permissions.value)
      .filter(([, allowed]) => allowed)
      .map(([key]) => key);
  };

  /**
   * เช็คว่า user มีสิทธิ์เข้าถึงแพลตฟอร์มหรือไม่
   * @param {string} platformId - ID ของแพลตฟอร์ม
   * @returns {boolean}
   */
  const hasPermission = (platformId) => {
    return permissions.value[platformId] !== false;
  };

  /**
   * กรอง array ของแพลตฟอร์มตาม permission
   * @param {Array} platforms - รายการแพลตฟอร์มทั้งหมด
   * @returns {Array} รายการแพลตฟอร์มที่ user มีสิทธิ์เข้าถึง
   */
  const filterByPermission = (platforms) => {
    return platforms.filter(platform => hasPermission(platform.id));
  };

  /**
   * Computed property สำหรับเช็คว่ามี permission อย่างน้อย 1 รายการ
   */
  const hasAnyPermission = computed(() => {
    return Object.values(permissions.value).some(v => v === true);
  });

  return {
    permissions,
    permissionsLoaded,
    loadPermissions,
    resetPermissions,
    getUserPermissions,
    hasPermission,
    filterByPermission,
    hasAnyPermission,
  };
}
